import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { CompanyOnboardingService } from '../services/company-onboarding.service';

const PHONE_PATTERN = /^(\+?1[\s-]?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/;

@Component({
  selector: 'app-admin-company-onboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, TitleBarComponent],
  templateUrl: './admin-company-onboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-company-onboard.component.scss'],
})
export class AdminCompanyOnboardComponent {
  private readonly fb = inject(FormBuilder);
  private readonly onboardingService = inject(CompanyOnboardingService);
  private readonly messageService = inject(NotificationService);
  private readonly router = inject(Router);

  submitting = false;
  selectedLogo: File | null = null;
  logoPreviewUrl: string | null = null;
  logoError = '';

  readonly form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    contact_number: ['', [Validators.required, Validators.pattern(PHONE_PATTERN)]],
    shop_name: ['', [Validators.required, Validators.minLength(1)]],
    shop_contact: ['', [Validators.required, Validators.pattern(PHONE_PATTERN)]],
    shop_email: ['', [Validators.required, Validators.email]],
  });

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.logoError = '';

    if (!file) {
      this.selectedLogo = null;
      this.logoPreviewUrl = null;
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.logoError = 'Logo must be an image file.';
      this.selectedLogo = null;
      this.logoPreviewUrl = null;
      input.value = '';
      return;
    }

    this.selectedLogo = file;
    this.logoPreviewUrl = URL.createObjectURL(file);
  }

  submit(): void {
    this.logoError = '';
    if (!this.selectedLogo) {
      this.logoError = 'Company logo is required.';
    }

    if (this.form.invalid || !this.selectedLogo || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = new FormData();
    const value = this.form.getRawValue();
    formData.append('name', value.name);
    formData.append('email', value.email);
    formData.append('password', value.password);
    formData.append('contact_number', value.contact_number);
    formData.append('role', 'admin');
    formData.append('shop_name', value.shop_name);
    formData.append('shop_contact', value.shop_contact);
    formData.append('shop_email', value.shop_email);
    formData.append('logo', this.selectedLogo, this.selectedLogo.name);

    this.submitting = true;
    this.onboardingService.registerCompany(formData).subscribe({
      next: res => {
        this.submitting = false;
        if (res.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Company onboarded successfully',
            life: 4000,
          });
          void this.router.navigate(['/admin/companies']);
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: res.message || 'Unable to onboard company.',
          life: 6000,
        });
      },
      error: err => {
        this.submitting = false;
        const detail =
          err?.error?.message ||
          err?.error?.errors?.email ||
          err?.message ||
          'Unable to onboard company.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: typeof detail === 'string' ? detail : 'Unable to onboard company.',
          life: 6000,
        });
      },
    });
  }

  showError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  controlError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors) {
      return '';
    }
    if (control.errors['required']) {
      return 'This field is required.';
    }
    if (control.errors['email']) {
      return 'Enter a valid email address.';
    }
    if (control.errors['minlength']) {
      const min = control.errors['minlength'].requiredLength;
      return `Must be at least ${min} characters.`;
    }
    if (control.errors['pattern']) {
      return 'Enter a valid phone number.';
    }
    return 'Invalid value.';
  }
}
